import React from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Chip, List, ListItemButton, ListItemIcon, ListItemText, Divider, Button } from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import LogoutIcon from '@mui/icons-material/Logout';
import { useAuthStore } from '../../store/auth';
import { BTMonitorPage } from './BTMonitorPage';
import { BTCdrPage } from './BTCdrPage';

/**
 * Standalone read-only shell for a carrier viewer (e.g. "BT User").
 * Exactly two menus: Dashboard and CDR (raw). No admin menus, no customer data.
 */
export const CarrierViewerShell: React.FC = () => {
  const logout = useAuthStore(s => s.logout);
  const user = useAuthStore(s => s.user);
  const nav = useNavigate();
  const loc = useLocation();

  const items = [
    { label: 'Dashboard', path: '/carrier-monitor', icon: <DashboardIcon /> },
    { label: 'CDR (raw)', path: '/carrier-monitor/cdr', icon: <ReceiptLongIcon /> },
  ];
  const isActive = (p: string) =>
    p === '/carrier-monitor'
      ? (loc.pathname === '/carrier-monitor' || loc.pathname === '/' || loc.pathname === '/dashboard')
      : loc.pathname.startsWith(p);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Box sx={{ width: 240, flexShrink: 0, bgcolor: '#0f172a', color: '#e2e8f0', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2.5, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#fff' }}>📞 BT Monitor</Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>Carrier traffic & settlement</Typography>
          <Box sx={{ mt: 1 }}><Chip size="small" label="Read-only" sx={{ bgcolor: '#1e293b', color: '#93c5fd' }} /></Box>
        </Box>
        <Divider sx={{ borderColor: '#1e293b' }} />
        <List sx={{ flexGrow: 1, py: 1 }}>
          {items.map(it => (
            <ListItemButton
              key={it.path}
              selected={isActive(it.path)}
              onClick={() => nav(it.path)}
              sx={{
                mx: 1, borderRadius: 2, mb: .5, color: '#cbd5e1',
                '&.Mui-selected': { bgcolor: '#6366F1', color: '#fff', '&:hover': { bgcolor: '#6366F1' } },
                '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 38 },
              }}
            >
              <ListItemIcon>{it.icon}</ListItemIcon>
              <ListItemText primary={it.label} />
            </ListItemButton>
          ))}
        </List>
        <Divider sx={{ borderColor: '#1e293b' }} />
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mb: 1 }}>
            {user?.first_name} {user?.last_name} · {user?.username}
          </Typography>
          <Button fullWidth startIcon={<LogoutIcon />} onClick={logout}
            sx={{ color: '#e2e8f0', borderColor: '#334155', justifyContent: 'flex-start' }} variant="outlined" size="small">
            Sign out
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minWidth: 0 }}>
        <Routes>
          <Route path="/carrier-monitor" element={<BTMonitorPage />} />
          <Route path="/carrier-monitor/cdr" element={<BTCdrPage />} />
          <Route path="*" element={<Navigate to="/carrier-monitor" replace />} />
        </Routes>
      </Box>
    </Box>
  );
};
